import { useEffect, useState } from "react";
import { adminListPosts, adminCreatePost, adminUpdatePost, adminDeletePost } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Plus, Trash2, Pencil, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { extractErrorMessage } from "../../lib/errors";
import RichTextEditor from "../../components/RichTextEditor";
import MediaPicker from "../../components/MediaPicker";

const empty = {
  title: "", slug: "", excerpt: "", body_html: "", featured_image: "",
  category: "General", meta_title: "", meta_description: "",
  meta_robots: "index,follow", schema_json: "", published: true,
};

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [featuredPickerOpen, setFeaturedPickerOpen] = useState(false);
  const load = async () => { try { setPosts(await adminListPosts()); } catch { toast.error("Load failed"); } };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.post_id) {
        await adminUpdatePost(editing.post_id, editing);
      } else {
        await adminCreatePost(editing);
      }
      toast.success("Saved");
      setEditing(null); await load();
    } catch (err) { toast.error(extractErrorMessage(err, "Save failed")); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try { await adminDeletePost(id); toast.success("Deleted"); await load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-blog-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl tracking-tight font-medium" style={{fontFamily:"Outfit, sans-serif"}}>Blog</h1>
          <p className="text-sm text-[#4B5563]">Create and manage blog posts. Each post is SEO-ready with custom meta and schema.</p>
        </div>
        <Button onClick={() => setEditing({ ...empty })} className="bg-[#37B160] hover:bg-[#2D9450] text-white" data-testid="blog-new-btn"><Plus className="h-4 w-4 mr-1" /> New post</Button>
      </div>
      <div className="mt-6 bg-white border border-black/5 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F3F4F6] text-[#4B5563] text-xs uppercase tracking-[0.15em]">
            <tr><th className="text-left px-4 py-3">Title</th><th className="text-left px-4 py-3">Slug</th><th className="text-left px-4 py-3">Status</th><th></th></tr>
          </thead>
          <tbody>
            {posts.map(p => (
              <tr key={p.post_id} className="border-t border-black/5" data-testid={`post-row-${p.slug}`}>
                <td className="px-4 py-3 font-medium">{p.title}</td>
                <td className="px-4 py-3 text-xs text-[#4B5563]">/blog/{p.slug}</td>
                <td className="px-4 py-3 text-xs">{p.published ? <span className="text-white bg-[#37B160] px-2 py-0.5 rounded">Live</span> : <span className="text-[#9CA3AF]">Draft</span>}</td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(p)} data-testid={`post-edit-${p.slug}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(p.post_id)} data-testid={`post-delete-${p.slug}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </td>
              </tr>
            ))}
            {posts.length === 0 && <tr><td colSpan="4" className="py-10 text-center text-[#9CA3AF]">No posts</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={o => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="blog-editor-modal">
          <DialogHeader><DialogTitle>{editing?.post_id ? "Edit post" : "New post"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3 text-sm">
              <Field label="Title"><Input value={editing.title} onChange={e => setEditing({...editing, title: e.target.value})} data-testid="post-title" /></Field>
              <Field label="Slug (URL)"><Input value={editing.slug} onChange={e => setEditing({...editing, slug: e.target.value})} placeholder="auto-generated if blank" data-testid="post-slug" /></Field>
              <Field label="Category"><Input value={editing.category} onChange={e => setEditing({...editing, category: e.target.value})} /></Field>
              <Field label="Featured image">
                <div className="flex gap-2 items-start">
                  <Input value={editing.featured_image} onChange={e => setEditing({...editing, featured_image: e.target.value})} placeholder="https://… or pick from library" data-testid="post-featured-image" />
                  <Button type="button" variant="outline" className="border-black/15 whitespace-nowrap" onClick={() => setFeaturedPickerOpen(true)} data-testid="post-featured-pick">
                    <ImageIcon className="h-4 w-4 mr-1" /> Library
                  </Button>
                </div>
                {editing.featured_image && (
                  <img src={editing.featured_image} alt="" className="mt-2 max-h-32 rounded-lg border border-black/5" />
                )}
              </Field>
              <Field label="Excerpt"><Textarea value={editing.excerpt} onChange={e => setEditing({...editing, excerpt: e.target.value})} rows={2} /></Field>
              <Field label="Body">
                <RichTextEditor value={editing.body_html} onChange={(html) => setEditing(prev => ({ ...prev, body_html: html }))} testId="post-body-editor" />
              </Field>
              <details className="bg-[#F3F4F6] rounded-xl p-3">
                <summary className="cursor-pointer text-xs uppercase tracking-[0.2em] text-[#4B5563]">SEO &amp; metadata</summary>
                <div className="mt-3 space-y-3">
                  <Field label="Meta title"><Input value={editing.meta_title} onChange={e => setEditing({...editing, meta_title: e.target.value})} /></Field>
                  <Field label="Meta description"><Textarea value={editing.meta_description} onChange={e => setEditing({...editing, meta_description: e.target.value})} rows={2} /></Field>
                  <Field label="Meta robots (e.g. index,follow)"><Input value={editing.meta_robots} onChange={e => setEditing({...editing, meta_robots: e.target.value})} /></Field>
                  <Field label="Schema JSON-LD"><Textarea value={editing.schema_json} onChange={e => setEditing({...editing, schema_json: e.target.value})} rows={4} placeholder='{"@context":"https://schema.org",...}' /></Field>
                </div>
              </details>
              <div className="flex items-center gap-2">
                <Switch checked={editing.published} onCheckedChange={v => setEditing({...editing, published: v})} data-testid="post-published" />
                <Label className="text-xs">Published</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="border-black/15">Cancel</Button>
            <Button onClick={save} className="bg-[#37B160] hover:bg-[#2D9450] text-white" data-testid="post-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MediaPicker
        open={featuredPickerOpen}
        onClose={() => setFeaturedPickerOpen(false)}
        onPick={(item) => { setEditing(prev => ({ ...prev, featured_image: item.url })); setFeaturedPickerOpen(false); }}
      />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-[#4B5563]">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
